export type TextContextCommand = 'cut' | 'copy' | 'paste' | 'selectAll';

export type ClipboardPort = {
  readText: () => Promise<string>;
  writeText: (text: string) => Promise<void>;
};

type TextControl = HTMLInputElement | HTMLTextAreaElement;

export type TextContext = {
  target: TextControl | HTMLElement | null;
  selectedText: string;
  selectionStart: number | null;
  selectionEnd: number | null;
  range: Range | null;
  canCut: boolean;
  canCopy: boolean;
  canPaste: boolean;
  canSelectAll: boolean;
};

type SelectionLike = Pick<Selection, 'toString'> & Partial<Pick<Selection, 'rangeCount' | 'getRangeAt'>>;

function isTextControl(target: EventTarget | null): target is TextControl {
  if (target instanceof HTMLTextAreaElement) return !target.disabled && !target.readOnly;
  if (!(target instanceof HTMLInputElement) || target.disabled || target.readOnly) return false;
  return !['button', 'checkbox', 'color', 'file', 'hidden', 'image', 'radio', 'range', 'reset', 'submit'].includes(target.type);
}

function editableAncestor(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof Element)) return null;
  return target.closest<HTMLElement>('[contenteditable]:not([contenteditable="false"])');
}

export function describeTextContext(
  eventTarget: EventTarget | null,
  selection: SelectionLike | null,
): TextContext | null {
  if (isTextControl(eventTarget)) {
    const start = eventTarget.selectionStart ?? 0;
    const end = eventTarget.selectionEnd ?? start;
    const selectedText = eventTarget.value.slice(start, end);
    return {
      target: eventTarget,
      selectedText,
      selectionStart: start,
      selectionEnd: end,
      range: null,
      canCut: selectedText.length > 0,
      canCopy: selectedText.length > 0,
      canPaste: true,
      canSelectAll: eventTarget.value.length > 0,
    };
  }

  const editable = editableAncestor(eventTarget);
  const selectedText = selection?.toString() ?? '';
  const range = selection?.rangeCount && selection.getRangeAt
    ? selection.getRangeAt(0).cloneRange()
    : null;
  if (editable) {
    return {
      target: editable,
      selectedText,
      selectionStart: null,
      selectionEnd: null,
      range,
      canCut: selectedText.length > 0,
      canCopy: selectedText.length > 0,
      canPaste: true,
      canSelectAll: editable.textContent !== '',
    };
  }

  if (!selectedText) return null;
  return {
    target: eventTarget instanceof HTMLElement ? eventTarget : null,
    selectedText,
    selectionStart: null,
    selectionEnd: null,
    range,
    canCut: false,
    canCopy: true,
    canPaste: false,
    canSelectAll: false,
  };
}

function setControlValue(control: TextControl, value: string) {
  const prototype = control instanceof HTMLTextAreaElement
    ? HTMLTextAreaElement.prototype
    : HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(prototype, 'value')?.set;
  setter?.call(control, value);
}

function replaceControlSelection(context: TextContext, replacement: string) {
  if (!isTextControl(context.target)) return;
  const start = context.selectionStart ?? 0;
  const end = context.selectionEnd ?? start;
  const value = `${context.target.value.slice(0, start)}${replacement}${context.target.value.slice(end)}`;
  setControlValue(context.target, value);
  const caret = start + replacement.length;
  context.target.focus();
  context.target.setSelectionRange(caret, caret);
  context.target.dispatchEvent(new InputEvent('input', {
    bubbles: true,
    inputType: replacement ? 'insertText' : 'deleteByCut',
    data: replacement || null,
  }));
}

function replaceEditableSelection(context: TextContext, replacement: string) {
  if (!context.target || !context.range) return;
  context.range.deleteContents();
  if (replacement) {
    const node = document.createTextNode(replacement);
    context.range.insertNode(node);
    context.range.setStartAfter(node);
    context.range.collapse(true);
  }
  context.target.dispatchEvent(new InputEvent('input', {
    bubbles: true,
    inputType: replacement ? 'insertText' : 'deleteByCut',
    data: replacement || null,
  }));
}

export async function runTextContextCommand(
  context: TextContext | null,
  command: TextContextCommand,
  clipboard: ClipboardPort,
): Promise<void> {
  if (!context) return;
  if (command === 'copy' && context.canCopy) {
    await clipboard.writeText(context.selectedText);
    return;
  }
  if (command === 'cut' && context.canCut) {
    await clipboard.writeText(context.selectedText);
    if (isTextControl(context.target)) replaceControlSelection(context, '');
    else replaceEditableSelection(context, '');
    return;
  }
  if (command === 'paste' && context.canPaste) {
    const text = await clipboard.readText();
    if (isTextControl(context.target)) replaceControlSelection(context, text);
    else replaceEditableSelection(context, text);
    return;
  }
  if (command === 'selectAll' && context.canSelectAll && context.target) {
    context.target.focus();
    if (isTextControl(context.target)) {
      context.target.setSelectionRange(0, context.target.value.length);
    } else {
      const range = document.createRange();
      range.selectNodeContents(context.target);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  }
}
